import React, { useContext, useState, useMemo, useEffect } from 'react';
import {
  Autocomplete,
  Alert,
  Button,
  Box,
  CircularProgress,
  Grid,
  Paper,
  Step,
  StepButton,
  Stepper,
  StepLabel,
  TextField,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import { toast } from 'react-toastify';
import { parse, format } from 'date-fns';

// Custom components
import DataContext from '../../../Contexts/DataContext';
import { useAuth } from '../../../Contexts/AuthContext';
import { getMonthsBetweenDates, getNextExcelColumn } from '../../../Helpers/utils';
import { getProjectDepartments, getProjectAccounts, getAccountFullDetails } from '../../../Helpers/dataApi';
import { calculateAccountMonthlyValues } from './calculations';

import ModelAfterGrid from './ModelAfterGrid';
import ProFormaGrid from './ProFormaGrid';
import StatDriverInput from './StatDriverInput';
import CreateEntityDialog from './CreateEntityDialog';
import CreateDepartmentDialog from './CreateDepartmentDialog';
import SeedAccountsPanel from './SeedAccountsPanel';

const STEPS = [
  'Select Model After',
  'Select Pro Forma',
  'Configure Stat Driver',
  'Configure Accounts',
  'Review & Submit'
];

// Helper to map API fixed/variable values to dropdown codes
const mapFixedOrVariable = (value) => {
  if (!value) return '';
  const normalized = String(value).toLowerCase();
  if (normalized === 'fixed' || normalized === 'f') return 'F';
  if (normalized === 'variable' || normalized === 'v') return 'V';
  return ''; // Default for 'Stat' or any other value
};

export default function AddProForma(props) {
  const auth = useAuth();
  const activeUser = auth.activeUser;
  const context = useContext(DataContext);
  
  const { addChange, cancelChange, toggleEditing, changeToEdit, editChange } = props;
  
  // Whether we are editing an existing pro forma (vs creating a new one)
  const isEditMode = Boolean(changeToEdit);
  
  const {
    activeProject,
    activeProjectAttributes,
    activeProjectInfo,
  } = context;

  // Wizard step state
  const [activeStep, setActiveStep] = useState(0);
  
  // Loading state
  const [isLoading, setIsLoading] = useState(false);

  // Model After state (reference entity/department to copy from)
  const [modelAfter, setModelAfter] = useState({
    entity: null,
    department: null,
    accounts: [], // Will contain base period data
  });

  // Pro Forma state (new entity/department being forecasted)
  const [proForma, setProForma] = useState({
    entity: null,
    department: null,
    name: '',
    accounts: [], // To be forecasted with Fixed/Variable designation
  });

  // Parent Change state — free-text or selected from existing change labels
  const [parentChange, setParentChange] = useState('');

  // Parent change options sourced from project's existing change labels (same as regular change form)
  const parentChangeOptions = useMemo(() => {
    return (activeProjectAttributes?.parent_changes || []).map(pc => pc.label);
  }, [activeProjectAttributes?.parent_changes]);

  // Stat Driver state
  const [statDriver, setStatDriver] = useState({
    account: null, // Selected stat driver account
    basePeriodTotal: 0,
    forecastMonthly: {}, // { 'Jan 2025': 500, 'Feb 2025': 450, ... }
  });

  // Manual overrides tracking
  const [manualOverrides, setManualOverrides] = useState({}); // { glKey: { 'Jan 2025': value, ... } }

  // Entity/Department options
  const [modelAfterOptions, setModelAfterOptions] = useState({
    entities: activeProjectAttributes.entities || [],
    departments: [],
  });

  const [proFormaOptions, setProFormaOptions] = useState({
    entities: activeProjectAttributes.entities || [],
    departments: [],
  });

  // Dialog open/close flags for inline creation
  const [createEntityOpen_modelAfter, setCreateEntityOpen_modelAfter] = useState(false);
  const [createDeptOpen_modelAfter, setCreateDeptOpen_modelAfter] = useState(false);
  const [createEntityOpen_proForma, setCreateEntityOpen_proForma] = useState(false);
  const [createDeptOpen_proForma, setCreateDeptOpen_proForma] = useState(false);

  // Seed accounts created inline (separate from proForma.accounts which comes from API)
  const [seedAccounts, setSeedAccounts] = useState([]);

  // Parent accounts for seed account form
  const [parentAccounts, setParentAccounts] = useState([]);

  // Load parent accounts on mount
  useEffect(() => {
    const loadParentAccounts = async () => {
      try {
        const result = await context.getParentAccounts();
        setParentAccounts(result || []);
      } catch (err) {
        console.error('[ProFormaV2] Error loading parent accounts:', err);
      }
    };
    loadParentAccounts();
  }, []);

  // Get forecast months from project date range
  const forecastMonths = useMemo(() => {
    if (!activeProjectInfo?.start_date || !activeProjectInfo?.end_date) return [];
    return getMonthsBetweenDates(activeProjectInfo.start_date, activeProjectInfo.end_date);
  }, [activeProjectInfo?.start_date, activeProjectInfo?.end_date]);

  // Recalculate all Pro Forma accounts when stat driver changes
  useEffect(() => {
    if (!statDriver.account || Object.keys(statDriver.forecastMonthly).length === 0) {
      return; // No stat driver configured yet
    }

    // Use functional update to avoid stale `proForma` and dropped concurrent updates
    setProForma(prevProForma => {
      if (!prevProForma || !prevProForma.accounts || prevProForma.accounts.length === 0) {
        return prevProForma; // No accounts loaded yet
      }

      // Recalculate all accounts that have Fixed or Variable set
      const updatedAccounts = prevProForma.accounts.map(acc => {
        if (!acc.fixedOrVariable) {
          return acc; // Skip accounts without F/V designation
        }

        const calculatedMonthly = calculateAccountMonthlyValues(
          { basePeriodTotal: acc.basePeriodTotal },
          acc.fixedOrVariable,
          forecastMonths,
          statDriver
        );

        return {
          ...acc,
          calculatedMonthly,
        };
      });

      return {
        ...prevProForma,
        accounts: updatedAccounts,
      };
    });
  }, [
    statDriver.account,
    statDriver.basePeriodTotal,
    statDriver.forecastMonthly,
    forecastMonths,
  ]); // Recalc when any relevant stat driver value or forecast months change

  // ===============================
  // Edit Mode: Pre-populate wizard state from changeToEdit
  // ===============================
  // all wizard state from the saved change object and start at Step 1 (Pro Forma
  // entity/dept). Step 0 (Model After) is skipped in edit mode since the accounts
  // are already known from the saved reference_accounts.
  // ===============================
  useEffect(() => {
    if (!changeToEdit || !forecastMonths.length) return;

    const refAccounts = changeToEdit.reference_accounts || [];

    // ── Name & parent change ───────────────────────────────────────────────
    setParentChange(changeToEdit.parent_change || '');

    // ── Identify stat driver and pro forma account refs ────────────────────
    // Stat driver: flagged is_stat_driver, or rowLetter 'A' as fallback for
    // older pro formas that didn't set the flag.
    const statDriverRef = refAccounts.find(ra => ra.is_stat_driver)
      || (refAccounts.length > 0 && refAccounts[0].rowLetter === 'A' ? refAccounts[0] : null);

    const proFormaRefs = refAccounts.filter(ra =>
      ra !== statDriverRef && !ra.is_stat_driver
    );

    // ── Stat driver state ──────────────────────────────────────────────────
    if (statDriverRef) {
      const forecastMonthlyMap = {};
      (statDriverRef.monthly_values || []).forEach(mv => {
        if (mv.month_year) forecastMonthlyMap[mv.month_year] = mv.value || 0;
      });
      // Build a minimal account object so StatDriverInput can display the selection.
      // Derive a readable name from the GL key parts: "entityId-deptId-accountId"
      const parts = statDriverRef.glKey?.split('-') || [];
      const statDriverAccount = {
        glKey: statDriverRef.glKey,
        account_id: parts[2] || statDriverRef.glKey || '',
        // Use account_name from the reference account if available (newer saves),
        // otherwise fall back to the GL key so the label isn't empty.
        account_name: statDriverRef.account_name || statDriverRef.glKey || '',
        basePeriodTotal: 0,
        dates: statDriverRef.dates || {},
        parent_account: null,
      };
      setStatDriver({
        account: statDriverAccount,
        basePeriodTotal: 0,
        forecastMonthly: forecastMonthlyMap,
      });
      // Seed modelAfter.accounts with the stat driver so Step 2 has an option to
      // show in the dropdown. The user can go back to Step 0 to load a full
      // Model After if they need to pick a different stat driver.
      setModelAfter(prev => ({
        ...prev,
        accounts: [statDriverAccount],
      }));
    }

    // ── Pro forma entity/dept from GL keys ─────────────────────────────────
    // GL key format: entityId-deptId-accountId
    const firstRef = proFormaRefs[0] || statDriverRef;
    let entityId = null;
    let departmentId = null;
    if (firstRef?.glKey) {
      const parts = firstRef.glKey.split('-');
      if (parts.length >= 3) {
        entityId = parts[0];
        departmentId = parts[1];
      }
    }

    // ── Pro forma accounts ─────────────────────────────────────────────────
    const restoredAccounts = proFormaRefs.map(ra => {
      // is_fixed_value: true → 'F', false → 'V', null → ''
      // Note: for old pro formas where all accounts have is_fixed_value:false,
      // this maps to 'V'. The user can adjust per account in Step 3.
      const fixedOrVariable = ra.is_fixed_value === true ? 'F'
        : ra.is_fixed_value === false ? 'V'
        : '';

      const calculatedMonthly = {};
      const accountManualOverrides = {};
      (ra.monthly_values || []).forEach(mv => {
        if (mv.month_year) {
          calculatedMonthly[mv.month_year] = mv.value || 0;
          if (mv.is_manually_entered) {
            accountManualOverrides[mv.month_year] = mv.value || 0;
          }
        }
      });

      const parts = ra.glKey?.split('-') || [];
      return {
        account_id: parts[2] || '',
        account_name: ra.glKey || '',
        glKey: ra.glKey,
        fixedOrVariable,
        basePeriodTotal: 0,
        calculatedMonthly,
        _manualOverrides: accountManualOverrides,
      };
    });

    // ── Set pro forma state ────────────────────────────────────────────────
    setProForma(prev => ({
      ...prev,
      name: changeToEdit.change_label || '',
      entity: entityId,
      department: departmentId,
      accounts: restoredAccounts,
    }));

    // Restore manual overrides map
    const overridesByGlKey = {};
    restoredAccounts.forEach(acc => {
      if (Object.keys(acc._manualOverrides).length > 0) {
        overridesByGlKey[acc.glKey] = acc._manualOverrides;
      }
    });
    if (Object.keys(overridesByGlKey).length > 0) {
      setManualOverrides(overridesByGlKey);
    }

    // Pre-populate entity/dept dropdowns so the selectors show the saved values.
    // The entities list is already loaded from activeProjectAttributes; we just
    // need to trigger the departments load for the saved entity.
    if (entityId && activeProjectInfo?.id && activeUser?.accessToken) {
      getProjectDepartments(activeProjectInfo.id, entityId, activeUser.accessToken)
        .then(response => {
          if (response.status === 200) {
            const departments = response.data[0]?.departments_list || [];
            setProFormaOptions(prev => ({ ...prev, departments }));
          }
        })
        .catch(() => {}); // Silently ignore — user can re-select if needed
    }

    // Start at Step 1 (Pro Forma selection) — Model After step is skipped
    // in edit mode since the accounts already exist in the saved change.
    setActiveStep(1);
  }, [changeToEdit, forecastMonths.length]); // eslint-disable-line react-hooks/exhaustive-deps

  // ===============================
  // Step 1: Model After Entity/Department Selection
  // ===============================
  
  const handleModelAfterEntityChange = async (event, value) => {
    if (!value) {
      setModelAfter({ entity: null, department: null, accounts: [] });
      setModelAfterOptions(prev => ({ ...prev, departments: [] }));
      return;
    }

    // Clear stale data immediately to prevent dropdown showing old options
    setModelAfter({ entity: value.entity_id, department: null, accounts: [] });
    setModelAfterOptions(prev => ({ ...prev, departments: [] }));

    try {
      const response = await getProjectDepartments(
        activeProjectInfo.id,
        value.entity_id,
        activeUser.accessToken
      );
      
      if (response.status === 200) {
        // Response data is an array - extract departments_list from first element
        const departments = response.data[0]?.departments_list || [];
        setModelAfterOptions(prev => ({ ...prev, departments }));
      }
    } catch (error) {
      toast.error('Error loading departments');
    }
  };

  const handleModelAfterDepartmentChange = async (event, value) => {
    if (!value) {
      setModelAfter({ ...modelAfter, department: null, accounts: [] });
      return;
    }

    const departmentId = value.department_id;
    if (process.env.NODE_ENV !== 'production') {
      console.log('[ProFormaV2] Department selected:', { departmentId, currentEntity: modelAfter.entity });
    }
    
    // Check if entity is set before loading accounts
    if (!modelAfter.entity) {
      console.error('[ProFormaV2] Cannot load accounts: entity not set');
      toast.error('Please select an entity first');
      return;
    }
    
    setModelAfter({ ...modelAfter, department: departmentId });
    
    // Load accounts for this entity/department combination - use current entity value
    await loadModelAfterAccounts(modelAfter.entity, departmentId);
  };

  const loadModelAfterAccounts = async (entityId, departmentId) => {
    setIsLoading(true);
    try {
      if (process.env.NODE_ENV !== 'production') {
        console.log('[ProFormaV2] Loading Model After accounts for:', { entityId, departmentId });
      }
      
      const reqData = [{
        entity_id: entityId,
        departments_id: [departmentId],
      }];

      const response = await getProjectAccounts(
        activeProjectInfo.id,
        reqData,
        activeUser.accessToken
      );

      if (process.env.NODE_ENV !== 'production') {
        console.log('[ProFormaV2] Model After accounts response:', response);
        console.log('[ProFormaV2] Response data structure:', response.data);
      }

      if (response.status === 200) {
        // Response structure: { EntityName: { DepartmentName: [accounts...] } }
        const entityData = Object.values(response.data)[0]; // Get first entity
        if (process.env.NODE_ENV !== 'production') {
          console.log('[ProFormaV2] Entity data:', entityData);
        }
        
        // Get the first department's accounts array (there should only be one based on our request)
        const accountsList = Object.values(entityData)[0] || [];
        if (process.env.NODE_ENV !== 'production') {
          console.log('[ProFormaV2] Accounts list from API:', accountsList, 'Length:', accountsList.length);
        }
        
        // Fetch full details for each account to get monthly values
        const accountsWithDetails = await Promise.all(
          accountsList.map(async (acc, index) => {
            const glKey = `${entityId}-${departmentId}-${acc.account_id}`;
            const detailsResponse = await getAccountFullDetails(
              activeProjectInfo.id,
              entityId,
              departmentId,
              acc.account_id,
              activeUser.accessToken
            );
            
            if (detailsResponse.status === 200) {
              // Response structure: { EntityName: { DepartmentName: [accountObj] } }
              const entityData = Object.values(detailsResponse.data)[0];
              const departmentData = Object.values(entityData)[0];
              const accountData = departmentData[0]; // First element of array
              
              // Calculate base period total and convert date format
              let basePeriodTotal = 0;
              const convertedDates = {};
              
              if (accountData?.dates) {
                Object.entries(accountData.dates).forEach(([dateKey, val]) => {
                  const numericValue = Number(val) || 0;
                  basePeriodTotal += numericValue;
                  
                  // Convert date from 'yyyy-MM-dd' to 'MMM yyyy' format
                  try {
                    const parsedDate = parse(dateKey, 'yyyy-MM-dd', new Date());
                    const formattedDate = format(parsedDate, 'MMM yyyy');
                    convertedDates[formattedDate] = numericValue;
                  } catch (e) {
                    // If parsing fails, try using the date key as-is
                    convertedDates[dateKey] = numericValue;
                  }
                });
              }
              
              return {
                account_id: acc.account_id,
                account_name: acc.account_name,
                glKey,
                basePeriodTotal,
                dates: convertedDates,
                fixed_or_variable: mapFixedOrVariable(accountData?.fixed_or_variable), // Map to 'F', 'V', or ''
              };
            }
            
            return {
              account_id: acc.account_id,
              account_name: acc.account_name,
              glKey,
              basePeriodTotal: 0,
              dates: {},
              fixed_or_variable: null,
            };
          })
        );

        if (process.env.NODE_ENV !== 'production') {
          console.log('[ProFormaV2] Model After accounts loaded:', accountsWithDetails.length);
        }
        setModelAfter({ entity: entityId, department: departmentId, accounts: accountsWithDetails });
        toast.success(`Loaded ${accountsWithDetails.length} Model After accounts`);
      } else {
        toast.error(`Failed to load Model After accounts: ${response.status}`);
      }
    } catch (error) {
      console.error('[ProFormaV2] Error loading Model After accounts:', error);
      toast.error('Error loading Model After accounts');
    } finally {
      setIsLoading(false);
    }
  };

  // ===============================
  // Step 2: Pro Forma Entity/Department Selection
  // ===============================
  
  const handleProFormaEntityChange = async (event, value) => {
    if (!value) {
      setProForma(prev => ({ ...prev, entity: null, department: null, accounts: [] }));
      setProFormaOptions(prev => ({ ...prev, departments: [] }));
      return;
    }

    // Clear stale data immediately to prevent dropdown showing old options
    setProForma(prev => ({ ...prev, entity: value.entity_id, department: null, accounts: [] }));
    setProFormaOptions(prev => ({ ...prev, departments: [] }));

    try {
      const response = await getProjectDepartments(
        activeProjectInfo.id,
        value.entity_id,
        activeUser.accessToken
      );
      
      if (response.status === 200) {
        // Response data is an array - extract departments_list from first element
        const departments = response.data[0]?.departments_list || [];
        setProFormaOptions(prev => ({ ...prev, departments }));
      }
    } catch (error) {
      toast.error('Error loading departments');
    }
  };

  const handleProFormaDepartmentChange = async (event, value) => {
    if (!value) {
      setProForma({ ...proForma, department: null, accounts: [] });
      return;
    }

    const departmentId = value.department_id;
    setProForma({ ...proForma, department: departmentId });
    
    // Load accounts for this entity/department combination - use current entity value
    await loadProFormaAccounts(proForma.entity, departmentId);
  };

  const loadProFormaAccounts = async (entityId, departmentId) => {
    setIsLoading(true);
    try {
      if (process.env.NODE_ENV !== 'production') {
        console.log('[ProFormaV2] Loading Pro Forma accounts for:', { entityId, departmentId });
      }
      
      const reqData = [{
        entity_id: entityId,
        departments_id: [departmentId],
      }];

      const response = await getProjectAccounts(
        activeProjectInfo.id,
        reqData,
        activeUser.accessToken
      );

      if (process.env.NODE_ENV !== 'production') {
        console.log('[ProFormaV2] Pro Forma accounts response:', response);
      }

      if (response.status === 200) {
        // Response structure: { EntityName: { DepartmentName: [accounts...] } }
        const entityData = Object.values(response.data)[0];
        const accountsList = Object.values(entityData)[0] || [];
        if (process.env.NODE_ENV !== 'production') {
          console.log('[ProFormaV2] Loaded Pro Forma accounts count:', accountsList.length);
        }
        
        // Initialize accounts with Fixed/Variable designation pre-filled from Model After
        const accountsWithDefaults = accountsList.map(acc => {
          // Try to find matching Model After account by account_id to get Fixed/Variable
          const modelAfterAccount = modelAfter.accounts.find(ma => ma.account_id === acc.account_id);
          
          // Use fixed_or_variable from the account data or from Model After match
          // Map API values to dropdown codes: 'F', 'V', or ''
          let initialFixedOrVariable = '';
          if (acc.fixed_or_variable) {
            initialFixedOrVariable = mapFixedOrVariable(acc.fixed_or_variable);
          } else if (modelAfterAccount && modelAfterAccount.fixed_or_variable) {
            // Get from Model After if account matches (already mapped)
            initialFixedOrVariable = modelAfterAccount.fixed_or_variable;
          }
          
          const basePeriodTotal = modelAfterAccount?.basePeriodTotal || 0;
          
          // Calculate initial monthly values if Fixed/Variable is set and stat driver exists
          let calculatedMonthly = {};
          if (initialFixedOrVariable && statDriver.account && Object.keys(statDriver.forecastMonthly).length > 0) {
            calculatedMonthly = calculateAccountMonthlyValues(
              { basePeriodTotal },
              initialFixedOrVariable,
              forecastMonths,
              statDriver
            );
          }
          
          return {
            account_id: acc.account_id,
            account_name: acc.account_name,
            glKey: `${entityId}-${departmentId}-${acc.account_id}`,
            fixedOrVariable: initialFixedOrVariable, // Pre-filled from Model After: 'F' or 'V'
            basePeriodTotal, // Copy from Model After
            calculatedMonthly, // Calculated based on Fixed/Variable and stat driver
          };
        });

        setProForma({ 
          entity: entityId, 
          department: departmentId, 
          name: proForma.name, 
          accounts: accountsWithDefaults 
        });
        
        toast.success(`Loaded ${accountsWithDefaults.length} accounts for Pro Forma configuration`);
      } else {
        toast.error(`Failed to load Pro Forma accounts: ${response.status}`);
      }
    } catch (error) {
      console.error('[ProFormaV2] Error loading Pro Forma accounts:', error);
      toast.error('Error loading Pro Forma accounts');
    } finally {
      setIsLoading(false);
    }
  };

  // ===============================
  // Step 3: Stat Driver Configuration
  // ===============================
  
  const handleStatDriverSelect = (account) => {
    // Guard against null account (when Autocomplete is cleared)
    if (!account) {
      setStatDriver({
        account: null,
        basePeriodTotal: 0,
        forecastMonthly: {},
      });
      return;
    }
    
    // Find the account in modelAfter to get base period total
    const modelAfterAccount = modelAfter.accounts.find(
      acc => acc.account_id === account.account_id
    );
    
    if (modelAfterAccount) {
      setStatDriver({
        account: modelAfterAccount,
        basePeriodTotal: modelAfterAccount.basePeriodTotal,
        forecastMonthly: {}, // Will be filled in by user
      });
    }
  };

  const handleStatDriverMonthlyChange = (month, value) => {
    setStatDriver(prev => ({
      ...prev,
      forecastMonthly: {
        ...prev.forecastMonthly,
        [month]: Number(value) || 0,
      },
    }));
  };

  // ===============================
  // Step 4: Account Configuration
  // ===============================
  
  const handleAccountFixedVariableChange = (accountGlKey, fixedOrVariable) => {
    const updatedAccounts = proForma.accounts.map(acc => {
      if (acc.glKey === accountGlKey) {
        // Find matching Model After account by account_id for reliable matching
        const modelAfterAccount = modelAfter.accounts.find(
          ma => ma.account_id === acc.account_id
        );
        
        const basePeriodTotal = modelAfterAccount?.basePeriodTotal || 0;
        
        // Calculate monthly values based on Fixed or Variable
        const calculatedMonthly = calculateAccountMonthlyValues(
          { basePeriodTotal },
          fixedOrVariable,
          forecastMonths,
          statDriver
        );
        
        return {
          ...acc,
          fixedOrVariable,
          basePeriodTotal,
          calculatedMonthly,
        };
      }
      return acc;
    });
    
    setProForma(prev => ({ ...prev, accounts: updatedAccounts }));
  };

  const handleManualOverride = (accountGlKey, month, value) => {
    // Use functional state update to avoid stale closures
    setManualOverrides(prevManualOverrides => {
      const nextOverrides = { ...(prevManualOverrides || {}) };
      const currentAccountOverrides = { ...(nextOverrides[accountGlKey] || {}) };

      // If the user clears the cell, remove the override for that month
      if (value === '' || value === null || value === undefined) {
        delete currentAccountOverrides[month];

        // If no overrides remain for this account, remove the account key entirely
        if (Object.keys(currentAccountOverrides).length === 0) {
          delete nextOverrides[accountGlKey];
        } else {
          nextOverrides[accountGlKey] = currentAccountOverrides;
        }

        return nextOverrides;
      }

      const numericValue = Number(value);

      // Do not store non-finite numbers; leave previous overrides unchanged
      if (!Number.isFinite(numericValue)) {
        return prevManualOverrides;
      }

      nextOverrides[accountGlKey] = {
        ...currentAccountOverrides,
        [month]: numericValue,
      };

      return nextOverrides;
    });
  };

  // ===============================
  // Step 5: Review & Submit
  // ===============================
  
  const handleSubmit = async () => {
    // Validate
    if (!proForma.name || !proForma.name.trim()) {
      toast.error('Please enter a Pro Forma name');
      return;
    }

    if (proForma.accounts.filter(acc => acc.fixedOrVariable).length === 0) {
      toast.error('Please configure at least one account with Fixed or Variable');
      return;
    }

    // Validate stat driver: if selected, must have forecast values
    if (statDriver.account && Object.keys(statDriver.forecastMonthly).length === 0) {
      toast.error('Please enter forecast monthly values for the stat driver');
      return;
    }

    try {
      // Prepare reference accounts data
      const referenceAccountsData = [];
      
      // 1. Add stat driver account as first reference account (always include if selected)
      if (statDriver.account) {
        const statDriverMonthlyValues = forecastMonths.map(month => ({
          month_year: month,
          value: statDriver.forecastMonthly[month] || 0, // Default to 0 if not entered
          is_formula: false,
          formula_text: null,
          is_manually_entered: true, // User entered these values
        }));

        referenceAccountsData.push({
          glKey: statDriver.account.glKey,
          rowLetter: 'A', // Stat driver gets first row letter
          is_fixed_value: null, // Stat driver is neither fixed nor variable
          monthly_values: statDriverMonthlyValues,
        });
      }
      
      // 2. Add Fixed and Variable accounts
      // If stat driver row was added, start from 'A' (will advance to 'B' for first account)
      // If no stat driver, start from '' (will advance to 'A' for first account)
      let currentRowLetter = statDriver.account ? 'A' : '';
      const fixedVariableAccounts = proForma.accounts
        .filter(acc => acc.fixedOrVariable && acc.glKey !== statDriver.account?.glKey) // Exclude stat driver from Fixed/Variable list
        .map(acc => {
          // Generate row letter (B, C, D, etc. since A is used by stat driver)
          currentRowLetter = getNextExcelColumn(currentRowLetter);
          const rowLetter = currentRowLetter;
          
          const isFixedValue = acc.fixedOrVariable === 'F';
          
          // Build monthly values array
          const monthlyValues = forecastMonths.map(month => {
            // Use manual override if exists, otherwise use calculated value
            const value = manualOverrides[acc.glKey]?.[month] !== undefined
              ? manualOverrides[acc.glKey][month]
              : acc.calculatedMonthly[month] || 0;
            
            const isManuallyEntered = manualOverrides[acc.glKey]?.[month] !== undefined;
            
            return {
              month_year: month,
              value: value,
              is_formula: false,
              formula_text: null,
              is_manually_entered: isManuallyEntered,
            };
          });

          return {
            glKey: acc.glKey,
            rowLetter: rowLetter,
            is_fixed_value: isFixedValue,
            monthly_values: monthlyValues,
          };
        });
      
      referenceAccountsData.push(...fixedVariableAccounts);

      // Prepare change data to match backend structure
      const changeData = {
        change_label: proForma.name,
        date_range: [activeProjectInfo.start_date, activeProjectInfo.end_date],
        date_range_select: '',
        driven_accounts: [],
        function_operator: 'account',
        is_dominant_change: false,
        is_stat_driver: true,
        operator_value: '',
        ref_account_formula: '',
        // Include all affected GL keys in selected_accounts for calculated account propagation
        selected_accounts: referenceAccountsData.map(ref => ref.glKey),
        user_note: '',
        parent_change: parentChange || '',
        user_id: activeUser.id,
        project_id: activeProject.id,
        stat_driver_gl_key: statDriver.account?.glKey || null,
        reference_accounts: referenceAccountsData,
      };

      if (process.env.NODE_ENV !== 'production') {
        // Log summary instead of full data (can be huge)
        console.log('[ProFormaV2] Change data summary:', {
          selectedAccountsCount: changeData.selected_accounts?.length,
          referenceAccountsCount: referenceAccountsData?.length,
          hasStatDriver: Boolean(changeData.stat_driver_gl_key),
          proFormaName: changeData.change_label,
        });
      }

      // Submit via addChange (POST) for new, editChange (PUT) for existing
      let success;
      if (isEditMode && editChange) {
        const editPayload = { ...changeData, id: changeToEdit.id };
        success = await editChange(editPayload);
      } else {
        success = await addChange(changeData);
      }
      
      // Only close form and show success if submission succeeded
      if (success) {
        // Close form
        if (toggleEditing) {
          toggleEditing();
        }
        
        toast.success(isEditMode ? 'Pro Forma updated successfully!' : 'Pro Forma saved successfully!');
      } else {
        // Error already handled by addChange, but we can add additional UI feedback here if needed
        console.error('[ProFormaV2] Change submission returned false');
      }
    } catch (error) {
      toast.error('Error saving Pro Forma. Please try again.');
      console.error('Pro Forma submission error:', error);
    }
  };

  // ===============================
  // Navigation helpers
  // ===============================
  
  const handleNext = () => {
    // In edit mode the model-after and entity/dept steps are pre-populated from
    // the existing change, so skip those validations when advancing from step 0/1.
    if (!isEditMode) {
      if (activeStep === 0 && (!modelAfter.entity || !modelAfter.department)) {
        toast.warn('Please select Model After entity and department');
        return;
      }
      if (activeStep === 1 && (!proForma.entity || !proForma.department)) {
        toast.warn('Please select Pro Forma entity and department');
        return;
      }
      if (activeStep === 1 && proForma.accounts.length === 0 && seedAccounts.length === 0) {
        toast.warn('Please add at least one account before proceeding');
        return;
      }
    } else {
      // In edit mode, still validate entity/dept on Step 1 since the user can
      // change them, but allow proceeding if accounts are already populated.
      if (activeStep === 1 && (!proForma.entity || !proForma.department)) {
        toast.warn('Please select Pro Forma entity and department');
        return;
      }
      if (activeStep === 1 && proForma.accounts.length === 0 && seedAccounts.length === 0) {
        toast.warn('Please add at least one account before proceeding');
        return;
      }
    }
    if (activeStep === 2 && !statDriver.account) {
      toast.warn('Please select a stat driver account');
      return;
    }
    
    setActiveStep(prev => Math.min(prev + 1, STEPS.length - 1));
  };

  const handleBack = () => {
    setActiveStep(prev => Math.max(prev - 1, 0));
  };

  const handleCancel = () => {
    if (cancelChange) {
      cancelChange();
    }
  };

  // ===============================
  // Render
  // ===============================
  
  return (
    <Paper
      data-testid="pro-forma-form"
      elevation={3}
      sx={{
        padding: '20px',
        minHeight: '500px',
      }}
    >
      <Typography variant="h5" gutterBottom>
        {isEditMode ? 'Edit Pro Forma' : 'Add Pro Forma (V2)'}
      </Typography>
      
      <Stepper activeStep={activeStep} sx={{ mt: 2, mb: 4 }} nonLinear>
        {STEPS.map((label, index) => (
          <Step key={label} completed={index < activeStep}>
            <StepButton onClick={() => setActiveStep(index)}>
              {isEditMode && index === 0 ? `${label} (optional)` : label}
            </StepButton>
          </Step>
        ))}
      </Stepper>

      {/* Loading Indicator */}
      {isLoading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', my: 3 }}>
          <CircularProgress />
        </Box>
      )}

      {/* Step 0: Model After Selection */}
      {activeStep === 0 && (
        <Box>
          <Typography variant="h6" gutterBottom>
            Step 1: Select Model After (Reference)
          </Typography>
          <Typography variant="body2" color="text.secondary" gutterBottom>
            Choose the entity and department to use as a reference for your pro forma.
          </Typography>
          
          <Grid container spacing={2} sx={{ mt: 2 }}>
            <Grid item xs={12} md={6}>
              <Autocomplete
                options={modelAfterOptions.entities}
                getOptionLabel={(option) => option.entity_name || ''}
                value={modelAfterOptions.entities.find(e => e.entity_id === modelAfter.entity) || null}
                onChange={handleModelAfterEntityChange}
                renderInput={(params) => (
                  <TextField {...params} label="Model After Entity" data-testid="reference-entity-select" />
                )}
              />
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.75 }}>
                <Typography variant="caption" color="text.disabled">or</Typography>
                <Button
                  size="small"
                  startIcon={<AddIcon />}
                  onClick={() => setCreateEntityOpen_modelAfter(true)}
                  sx={{ textTransform: 'none', color: 'text.secondary', fontWeight: 400 }}
                >
                  Add Entity
                </Button>
              </Box>
            </Grid>
            
            <Grid item xs={12} md={6}>
              <Autocomplete
                options={modelAfterOptions.departments}
                getOptionLabel={(option) => option.department_id && option.department_name ? `${option.department_id} - ${option.department_name}` : (option.department_name || '')}
                value={modelAfterOptions.departments.find(d => d.department_id === modelAfter.department) || null}
                onChange={handleModelAfterDepartmentChange}
                disabled={!modelAfter.entity}
                renderInput={(params) => (
                  <TextField {...params} label="Model After Department" data-testid="reference-department-select" />
                )}
              />
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.75 }}>
                <Typography variant="caption" color="text.disabled">or</Typography>
                <Button
                  size="small"
                  startIcon={<AddIcon />}
                  onClick={() => setCreateDeptOpen_modelAfter(true)}
                  disabled={!modelAfter.entity}
                  sx={{ textTransform: 'none', color: 'text.secondary', fontWeight: 400 }}
                >
                  Add Department
                </Button>
              </Box>
            </Grid>
          </Grid>

          <CreateEntityDialog
            open={createEntityOpen_modelAfter}
            onClose={() => setCreateEntityOpen_modelAfter(false)}
            onCreated={(newEntity) => {
              setModelAfterOptions(prev => ({ ...prev, entities: [...prev.entities, newEntity] }));
              setModelAfter(prev => ({ ...prev, entity: newEntity.entity_id, department: null, accounts: [] }));
              setModelAfterOptions(prev => ({ ...prev, departments: [] }));
              setCreateEntityOpen_modelAfter(false);
            }}
          />
          <CreateDepartmentDialog
            open={createDeptOpen_modelAfter}
            entityId={modelAfter.entity}
            entityName={modelAfterOptions.entities.find(e => e.entity_id === modelAfter.entity)?.entity_name || ''}
            onClose={() => setCreateDeptOpen_modelAfter(false)}
            onCreated={(newDept) => {
              setModelAfterOptions(prev => ({ ...prev, departments: [...prev.departments, newDept] }));
              setModelAfter(prev => ({ ...prev, department: newDept.department_id }));
              setCreateDeptOpen_modelAfter(false);
            }}
          />

          {modelAfter.accounts.length > 0 && (
            <Box sx={{ mt: 3 }}>
              <Typography variant="subtitle1" gutterBottom>
                Model After Accounts (Read-Only)
              </Typography>
              <ModelAfterGrid accounts={modelAfter.accounts} months={forecastMonths} />
            </Box>
          )}
        </Box>
      )}

      {/* Step 1: Pro Forma Selection */}
      {activeStep === 1 && (
        <Box>
          <Typography variant="h6" gutterBottom>
            Step 2: Select Pro Forma Entity/Department
          </Typography>
          <Typography variant="body2" color="text.secondary" gutterBottom>
            Choose the new entity and department you want to forecast.
          </Typography>
          
          <Grid container spacing={2} sx={{ mt: 2 }}>
            <Grid item xs={12} md={6}>
              <Autocomplete
                options={proFormaOptions.entities}
                getOptionLabel={(option) => option.entity_name || ''}
                value={proFormaOptions.entities.find(e => e.entity_id === proForma.entity) || null}
                onChange={handleProFormaEntityChange}
                renderInput={(params) => (
                  <TextField {...params} label="Pro Forma Entity" data-testid="proforma-entity-select" />
                )}
              />
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.75 }}>
                <Typography variant="caption" color="text.disabled">or</Typography>
                <Button
                  size="small"
                  startIcon={<AddIcon />}
                  onClick={() => setCreateEntityOpen_proForma(true)}
                  sx={{ textTransform: 'none', color: 'text.secondary', fontWeight: 400 }}
                >
                  Add Entity
                </Button>
              </Box>
            </Grid>
            
            <Grid item xs={12} md={6}>
              <Autocomplete
                options={proFormaOptions.departments}
                getOptionLabel={(option) => option.department_id && option.department_name ? `${option.department_id} - ${option.department_name}` : (option.department_name || '')}
                value={proFormaOptions.departments.find(d => d.department_id === proForma.department) || null}
                onChange={handleProFormaDepartmentChange}
                disabled={!proForma.entity}
                renderInput={(params) => (
                  <TextField {...params} label="Pro Forma Department" data-testid="proforma-department-select" />
                )}
              />
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.75 }}>
                <Typography variant="caption" color="text.disabled">or</Typography>
                <Button
                  size="small"
                  startIcon={<AddIcon />}
                  onClick={() => setCreateDeptOpen_proForma(true)}
                  disabled={!proForma.entity}
                  sx={{ textTransform: 'none', color: 'text.secondary', fontWeight: 400 }}
                >
                  Add Department
                </Button>
              </Box>
            </Grid>
          </Grid>

          <CreateEntityDialog
            open={createEntityOpen_proForma}
            onClose={() => setCreateEntityOpen_proForma(false)}
            onCreated={(newEntity) => {
              setProFormaOptions(prev => ({ ...prev, entities: [...prev.entities, newEntity] }));
              setProForma(prev => ({ ...prev, entity: newEntity.entity_id, department: null, accounts: [] }));
              setProFormaOptions(prev => ({ ...prev, departments: [] }));
              setSeedAccounts([]);
              setCreateEntityOpen_proForma(false);
            }}
          />
          <CreateDepartmentDialog
            open={createDeptOpen_proForma}
            entityId={proForma.entity}
            entityName={proFormaOptions.entities.find(e => e.entity_id === proForma.entity)?.entity_name || ''}
            onClose={() => setCreateDeptOpen_proForma(false)}
            onCreated={(newDept) => {
              setProFormaOptions(prev => ({ ...prev, departments: [...prev.departments, newDept] }));
              setProForma(prev => ({ ...prev, department: newDept.department_id, accounts: [] }));
              setSeedAccounts([]);
              setCreateDeptOpen_proForma(false);
            }}
          />

          {proForma.entity && proForma.department && proForma.accounts.length === 0 && (
            <Box sx={{ mt: 3 }}>
              <SeedAccountsPanel
                entityId={proForma.entity}
                entityName={proFormaOptions.entities.find(e => e.entity_id === proForma.entity)?.entity_name || ''}
                departmentId={proForma.department}
                departmentName={proFormaOptions.departments.find(d => d.department_id === proForma.department)?.department_name || ''}
                accounts={seedAccounts}
                parentAccounts={parentAccounts}
                onAccountAdded={(account) => setSeedAccounts(prev => [...prev, account])}
              />
            </Box>
          )}

          {proForma.accounts.length > 0 && (
            <Alert severity="info" sx={{ mt: 3 }}>
              Loaded {proForma.accounts.length} accounts for Pro Forma configuration
            </Alert>
          )}
        </Box>
      )}

      {/* Step 2: Stat Driver Configuration */}
      {activeStep === 2 && (
        <Box>
          <Typography variant="h6" gutterBottom>
            Step 3: Configure Stat Driver
          </Typography>
          <Typography variant="body2" color="text.secondary" gutterBottom>
            Select a stat driver account and enter forecast values for each month.
          </Typography>
          
          {/* Debug info */}
          {modelAfter.accounts.length === 0 && !isEditMode && (
            <Alert severity="warning" sx={{ mb: 2 }}>
              No Model After accounts available. Please go back to Step 1 and ensure accounts are loaded.
            </Alert>
          )}
          
          {modelAfter.accounts.length === 0 && isEditMode && (
            <Alert severity="info" sx={{ mb: 2 }}>
              Go back to Step 1 (Model After) to load accounts for a different stat driver, or keep the current selection below.
            </Alert>
          )}

          {modelAfter.accounts.length > 0 && !isEditMode && (
            <Alert severity="info" sx={{ mb: 2 }}>
              {modelAfter.accounts.length} Model After accounts available for stat driver selection
            </Alert>
          )}

          {isEditMode && statDriver.account && Object.keys(statDriver.forecastMonthly).length === 0 && (
            <Alert severity="info" sx={{ mb: 2 }}>
              Forecast values for this stat driver were not stored with the original pro forma. Enter new forecast values below.
            </Alert>
          )}
          
          <StatDriverInput
            accounts={modelAfter.accounts}
            statDriver={statDriver}
            months={forecastMonths}
            onAccountSelect={handleStatDriverSelect}
            onMonthlyValueChange={handleStatDriverMonthlyChange}
          />
        </Box>
      )}

      {/* Step 3: Account Configuration */}
      {activeStep === 3 && (
        <Box>
          <Typography variant="h6" gutterBottom>
            Step 4: Configure Pro Forma Accounts
          </Typography>
          <Typography variant="body2" color="text.secondary" gutterBottom>
            Set each account as Fixed or Variable, review calculated values, and make manual overrides if needed.
          </Typography>
          
          <ProFormaGrid
            accounts={proForma.accounts}
            months={forecastMonths}
            manualOverrides={manualOverrides}
            onFixedVariableChange={handleAccountFixedVariableChange}
            onManualOverride={handleManualOverride}
          />
        </Box>
      )}

      {/* Step 4: Review & Submit */}
      {activeStep === 4 && (
        <Box>
          <Typography variant="h6" gutterBottom>
            Step 5: Review & Submit
          </Typography>
          
          <Grid container spacing={2} sx={{ mt: 2 }}>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Pro Forma Name"
                value={proForma.name}
                onChange={(e) => setProForma({ ...proForma, name: e.target.value })}
                placeholder="Enter a descriptive name for this Pro Forma"
                data-testid="pro-forma-name-input"
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <Autocomplete
                freeSolo
                id="pro-forma-parent-change"
                options={parentChangeOptions}
                value={parentChange}
                onChange={(event, newValue) => setParentChange(newValue || '')}
                onInputChange={(event, newValue) => setParentChange(newValue || '')}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Parent Change"
                    placeholder="Select existing or type a new label"
                    helperText="Optional — group this pro forma under an existing change"
                  />
                )}
              />
            </Grid>
            
            <Grid item xs={12}>
              <Alert severity="info">
                <strong>Summary:</strong>
                <ul>
                  <li>Model After: Entity {modelAfter.entity}, Department {modelAfter.department}</li>
                  <li>Pro Forma: Entity {proForma.entity}, Department {proForma.department}</li>
                  <li>Stat Driver: {statDriver.account?.account_name || 'None'}</li>
                  <li>Accounts Configured: {proForma.accounts.filter(acc => acc.fixedOrVariable).length}</li>
                </ul>
              </Alert>
            </Grid>
          </Grid>
        </Box>
      )}

      {/* Navigation Buttons */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 4 }}>
        <Button onClick={handleCancel} variant="outlined" data-testid="cancel-button">
          Cancel
        </Button>
        
        <Box>
          {activeStep > 0 && (
            <Button onClick={handleBack} sx={{ mr: 1 }} disabled={isLoading}>
              Back
            </Button>
          )}
          
          {activeStep < STEPS.length - 1 ? (
            <Button 
              onClick={handleNext} 
              variant="contained"
              data-testid={activeStep === 1 ? "add-accounts-button" : undefined}
              disabled={isLoading}
            >
              Next
            </Button>
          ) : (
            <Button onClick={handleSubmit} variant="contained" color="primary" data-testid="submit-change-button" disabled={isLoading}>
              {isEditMode ? 'Update Pro Forma' : 'Submit Pro Forma'}
            </Button>
          )}
        </Box>
      </Box>
    </Paper>
  );
}
